import { VoidCallback, VoidCallback1 } from '@deqo/foundation';
import Mime from 'mime/lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { useEffectOnce } from 'react-use';
import slugify from 'slugify';
import styled, { css, keyframes } from 'styled-components';
import { telemetry } from '../../../@vendor/Posthog';
import { IdLabelAdapter } from '../../../CodesForUI';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { ClipboardService } from '../../../platform/Clipboard';
import { useResolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { FlexFrame } from '../../dashboard/elements/LayoutElements';
import { Popup } from '../../popups/Popup';
import { Button, Colors, Input, Select } from './../../../ui';
import { ExportFormat, ExportOutput } from './ExportFormat';
import { ExportConfiguration, ExportService } from './ExportService';
import { CsvExportFormat } from './formats/CsvExportFormat';
import { HtmlTableExportFormat } from './formats/HtmlTableExportFormat';
import { JsonExportFormat } from './formats/JsonExportFormat';
import { MarkdownExportFormat } from './formats/MarkdownExportFormat';

const XScrollbars = Scrollbars as any;

const flashAnim = keyframes`    
    from { opacity: .75; transform: scale(1); }
    to { opacity: 0; transform: scale(1.25); }
`;

const textAnim = keyframes`    
    0% { opacity: 0; }
    10% { opacity: .5; }
    75% { opacity: .5; }
    100% { opacity: 0; }
`;

const DataFrame = styled.div<{ flash?: boolean }>`
    position: relative;
    flex: 1 1 auto;
    padding: .25rem 0 0;
    background: ${Colors.hover};
    border-radius: 0 0 .25rem .25rem;
    border: solid .125rem ${Colors.border};
    border-top: none;

    ${p => !p.flash ? '' : css`
        &:after {
            z-index: 2;
            content: '';
            position: absolute;
            inset: -2rem 0;
            background: ${Colors.hover};
            animation: ${flashAnim} 300ms ease-out;
            animation-fill-mode: forwards;
            pointer-events: none;
        }
    `}
`;

const Message = styled.div<{ flash?: boolean }>`
    padding: 0.4rem ​1rem;
    font-size: 1.125rem;
    color: ${Colors.text};
    opacity: 0;

    ${p => !p.flash ? '' : css`
        opacity: .75;
        animation: ${textAnim} 2000ms ease-out;
        animation-fill-mode: forwards;
    `}
`;

const DataText = styled.div`
    padding: .5rem;
    font-family: 'Fira Code', 'Fira Mono', sans-serif;
    white-space: pre;
`;

const DataHtml = styled.div`
    padding: .5rem;

    td {
        padding-right: 2rem;
        padding-bottom: .5rem;
    }
`;

const DataPreview: LFC<{ output?: ExportOutput }> = ({ output }) => {
    if (output?.type === 'text/html') {
        // eslint-disable-next-line no-underscore-dangle
        const __html = output.data;
        return <DataHtml dangerouslySetInnerHTML={{ __html }} />;
    }
    if (output?.type === 'text/plain') {
        return <DataText>{output.data}</DataText>;
    }
    return null;
};

const ControlFrame = styled.div`
    border: solid .125rem ${Colors.border};
    border-bottom: none;
    border-radius: .25rem .25rem 0 0 ;
    background: ${Colors.hover};

    & > * {
        margin-left: -.125rem;
        margin-top: -.125rem;
        margin-right: -.125rem;
    }
`;

const ControlInner = styled.div`
    display: flex;
    margin-left: -.125rem;
    margin-top: -.125rem;
    margin-right: -.125rem;
    border-radius: .25rem .25rem 0 0 ;

    ${Select.Frame} {
        position: relative;
        z-index: 1;
        margin-left: -.125rem;
    }

    ${Select.InputBorder} {
        box-shadow: none;
    }

    ${Select.Input} {
        border-color: ${Colors.border};
        border-width: .125rem;
        border-radius: 0;
        box-shadow: none !important;
        line-height: 2rem !important;
        font-size: .875rem;
        min-width: 10rem;

        &:hover {
            border-color: ${Colors.gray300};
        }

        &:active {
            border-color: ${Colors.gray400};
        }
    }

    ${Select.MenuFrame} {
        &:after {
            top: -2.0675rem;
            left: .125rem;
            right: .125rem;
        }
    }

    ${Select.MenuItem} {
        color: ${Colors.gray600};
        padding: 0.5rem 2.25rem 0.5rem 0.75rem;    
        font-size: 1rem;
    }

    ${Select.MenuItemText} {
        font-weight: 450;
    }

    ${Input.Frame} {
        display: inline-block;
        vertical-align: top;
        background: white;
        border-radius: 0 0 0 .275rem;
        min-height: 2.275rem;
    }

    ${Input.Main} {
        min-height: 2.275rem;
    }

    ${Input.Field} {
        padding: 0.5rem 0.5rem;
    }

    ${Input.Border} {
        box-shadow: none;
        border-radius: 0 0 0 .275rem;
        border: solid .125rem ${Colors.border};
    }
`;

const MenuItemDesc = styled.div`
    font-size: 0.75rem;
    line-height: 1.75em;
    margin-top: .25rem;
    opacity: 0.9;
`;

const ControlLabel = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0 .75rem;
    height: 2.25rem;
    color: ${Colors.text};
    background: white;
    border: solid .125rem ${Colors.border};
    border-radius: .25rem 0 0 0;
    font-weight: 400;
    font-size: .875rem;
`;

const ControlButton = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 2.25rem;
    padding: 0 0.375rem;
    color: ${Colors.text};
    cursor: pointer;
    background: white;
    border: solid .125rem ${Colors.border};
    user-select: none;

    margin-left: -.125rem;
    &:first-child {
        margin-left: 0;
        border-radius: 0 0 0 .25rem;
    }

    &:last-child {
        border-radius: 0 .25rem 0 0;
    }

    &:hover {
        /* box-shadow: 0 0 0 1px ${Colors.border}; */
        border-color: ${Colors.gray300};
        z-index: 1;
    }

    &:active {
        background: ${Colors.active};
    }

    svg, img {
        position: relative;
    }

    span {
        margin: 0 .375rem;
        font-weight: 400;
        font-size: .875rem;
    }

`;

const formatters: ExportFormat[] = [
    new CsvExportFormat(),
    new HtmlTableExportFormat(),
    new MarkdownExportFormat(),
    new JsonExportFormat(),
];

const defaultFilename = (boardName: string) => {
    const date = new Intl.DateTimeFormat(navigator.language, { dateStyle: 'short' } as any).format(new Date());
    return formatFilename(`${boardName.slice(0, 12)} ${date}`);
};

const formatFilename = (input: string) => {
    return slugify(input + 'x').slice(0, -1);
};

export type ExportOuputViewProps = { config: ExportConfiguration, onBack: VoidCallback, onComplete: VoidCallback1<boolean> };
export const ExportOutputView: LFC<ExportOuputViewProps> = ({ config, onBack, onComplete }) => {

    const boardInfo = useResolve(EditorSession).get(BoardInfo);
    const clipboard = useResolve(ClipboardService);
    const exporter = useResolve(ExportService);

    const rawData = useMemo(() => exporter.export(config), [config]);
    const [format, setFormat] = useState(formatters[0]);
    const [filename, setFilename] = useState(defaultFilename(boardInfo.name));
    const [preview, setPreview] = useState(null as ExportOutput);
    const [flash, setFlash] = useState(false);

    useEffect(() => {
        const outputs = format.apply(rawData);
        const html = outputs.find(x => x.type === 'text/html');
        const plain = outputs.find(x => x.type === 'text/plain');
        const actual = outputs.find(x => x.type !== 'text/plain') ?? outputs[0];
        setPreview(html ?? plain ?? null);
        const ext = Mime.getExtension(actual?.type ?? '');
        if (actual) {
            setFilename(f =>
                f.includes('.') ? f.split(/\./g).slice(0, -1).concat([ext]).join('.') : [f, ext].join('.')
            );
        }
    }, [config, format]);

    useEffectOnce(() => {
        telemetry.emit('export.view');
    });

    const onCopy = useCallback(() => {
        const clip = ExportOutput.toClipData(format.apply(rawData));
        clipboard.write(clip);
        setFlash(true);
        telemetry.emit('export.copy');
    }, [config, format]);

    const onDownload = useCallback(() => {
        ExportOutput.saveAs(filename, format.apply(rawData));
        telemetry.emit('export.download');
    }, [config, format, filename]);

    const onName = useCallback((value: string) => setFilename(formatFilename(value)), []);

    useEffect(() => {
        if (flash) {
            const t = setTimeout(() => setFlash(false), 2000);
            return () => clearTimeout(t);
        }
    }, [flash]);

    return <FlexFrame>
        <ControlFrame>
            <ControlInner>
                <ControlLabel>Output Type:</ControlLabel>
                <Select adapter={IdLabelAdapter<ExportFormat>()}
                    width={24}
                    menuHeight={30}
                    items={formatters}
                    selected={format}
                    onChange={x => setFormat(x)}
                    renderItems={
                        (items, selectedItem, onSelect) => items.map(tm =>
                            <Select.MenuItem key={tm.id} onClick={onSelect(tm)}>
                                <Select.MenuItemText>{tm.label}</Select.MenuItemText>
                                <MenuItemDesc>{tm.desc}</MenuItemDesc>
                                {tm === selectedItem && <Select.MenuItemCheck />}
                            </Select.MenuItem>
                        )}
                />
            </ControlInner>
        </ControlFrame>
        <DataFrame flash={flash}>
            <XScrollbars>
                <DataPreview output={preview} />
            </XScrollbars>
        </DataFrame>
        <Popup.Actions>
            <Popup.ActionPull>
                <FlexFrame flex="row">
                    <Input value={filename} before="Filename:" onChange={onName} />
                    &nbsp;
                    <Button label="Download" variant="primary"
                        icon={() => <Icon size="1.25" src="deqo://icons/hero/download" />}
                        onAction={() => onDownload()} />
                    &nbsp;
                    <Button label="Copy" variant="primary"
                        icon={() => <Icon size="1.25" src="deqo://icons/hero/clipboard-copy" />}
                        onAction={() => onCopy()} />
                    <Message flash={flash}>Copied to clipboard.</Message>
                </FlexFrame>
            </Popup.ActionPull>
            <Button onAction={() => onBack()}>Back</Button>
            <Button onAction={() => onComplete(false)}>Done</Button>
        </Popup.Actions>
    </FlexFrame>;
};
